/**
 * The course frame's own words (F5): the session, team, details, connections
 * and Drive folder boxes on the course pages. One place, so the boxes, their
 * tests and the judge's specimen read the same sentence.
 */
import { TRAILING_ZONE_CAPTION } from "@/lib/course-schedule";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "A", "A and B", "A, B and C". */
export function list(items: string[]): string {
  return items.length <= 1 ? (items[0] ?? "") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

/** "18:00" or "18:00:00" as "6:00 PM", split so a range can say its meridiem once. */
function clock(value: string): { time: string; meridiem: string } | null {
  const match = /^(\d{1,2}):(\d{2})/.exec(value);
  if (!match) return null;
  const hours = Number(match[1]);
  return { time: `${hours % 12 || 12}:${match[2]}`, meridiem: hours < 12 ? "AM" : "PM" };
}

interface Meeting {
  meeting_weekdays?: number[] | null;
  meeting_starts_at?: string | null;
  meeting_ends_at?: string | null;
  meeting_days?: string | null;
  meeting_time?: string | null;
}

/** How a course meets, in the person's words: "Mon and Wed, 7:00 to 8:30 PM". Null when nothing is recorded. */
export function meetingLine(course: Meeting): string | null {
  const days = course.meeting_weekdays?.length
    ? list(course.meeting_weekdays.map((day) => WEEKDAYS[day]).filter(Boolean))
    : course.meeting_days?.trim()
      ? // Recorded free text, "Mon · Wed" or "Mon Wed": the days, joined in words.
        list(course.meeting_days.split(/\s*[·,/]\s*|\s+/).filter((day) => day && !/^(?:and|&)$/i.test(day)))
      : "";
  const from = course.meeting_starts_at ? clock(course.meeting_starts_at) : null;
  const to = course.meeting_ends_at ? clock(course.meeting_ends_at) : null;
  const time =
    from && to
      ? from.meridiem === to.meridiem
        ? `${from.time} to ${to.time} ${to.meridiem}`
        : `${from.time} ${from.meridiem} to ${to.time} ${to.meridiem}`
      : // The band owns the zone, so a recorded caption ("8:30 PM EST") isn't repeated, nor kept wrong in summer.
        (course.meeting_time ?? "").trim().replace(TRAILING_ZONE_CAPTION, "").trim().replace(/\s*[\u2013\u2014-]\s*/g, " to ");
  return [days, time].filter(Boolean).join(", ") || null;
}

/** A term's dates, "Sep 7 to Dec 18", read as calendar days (never shifted by a zone). Null without both. */
export function termDates(start: string | null | undefined, end: string | null | undefined): string | null {
  const day = (value: string | null | undefined) => {
    const match = value ? /^(\d{4})-(\d{2})-(\d{2})/.exec(value) : null;
    return match ? { year: match[1], text: `${MONTHS[Number(match[2]) - 1]} ${Number(match[3])}` } : null;
  };
  const from = day(start);
  const to = day(end);
  if (!from || !to) return null;
  return from.year === to.year ? `${from.text} to ${to.text}` : `${from.text}, ${from.year} to ${to.text}, ${to.year}`;
}

/** Online, in person (and where), or both. */
export function modalityLine(modality: string | null | undefined, location: string | null | undefined): string | null {
  const place = location?.trim() ? ` at ${location.trim()}` : "";
  if (modality === "online") return "Online";
  if (modality === "in_person") return `In person${place}`;
  if (modality === "hybrid") return `Online and in person${place}`;
  return null;
}

export const FRAME_COPY = {
  /** Exact work addressed over Desk or a session, while its sheet's read is not ready (M4-A2). */
  work: {
    loading: "Opening this work...",
    failed: "This work could not be opened. Try again.",
    refused: "This work is not available in this course.",
    retry: "Try again",
  },
  exactSession: {
    unavailable: "Session unavailable",
    failed: "This session could not be read. Try again.",
    refused: "This session is not available in this course.",
    retry: "Try again",
    return: "Return to course",
    loading: "Reading this session...",
  },
  session: {
    meets: (line: string) => `Meets ${line}.`,
  },
  team: {
    label: "Teaching team",
    job: "Say who teaches this course.",
    empty: "No teaching team is listed.",
    failed: "Couldn't read the teaching team",
    /** The team as one sentence: the primary instructor first, then whoever teaches with them. */
    teaches: (primary: string | null, others: string[]) =>
      primary
        ? `${primary} teaches this course${others.length ? `, with ${list(others)}` : ""}.`
        : `${list(others)} ${others.length === 1 ? "teaches" : "teach"} this course.`,
    message: "Message",
    primary: "Primary instructor",
    co: "Co-instructor",
    count: (n: number) => `${n} ${n === 1 ? "teacher" : "teachers"}`,
    /** An assigned administrator changes the team through the registrar's team visit. */
    change: "Change team",
  },
  publication: {
    /** An assigned administrator reviews publication and archive through the registrar's visit. */
    review: "Review publication",
  },
  details: {
    label: "Course details",
    job: "Say when the term runs and how the course meets.",
    none: "No term dates or meeting time are recorded.",
    failed: "Couldn't read the course details",
    edit: "Edit details",
  },
  connections: {
    label: "Course connections",
    job: "Say which provider capabilities are ready for this course, who owns each, and what fixes one that isn't.",
    unread: "Not checked",
    check: "Check connections",
    failed: "Couldn't read provider readiness",
    failedDetail: "No provider capability is inferred from the failed read.",
    stale: "Couldn't check again. This is the last answer.",
    answer: (ready: number, total: number) => `${ready} of ${total} ready for this course and your account.`,
    none: "No provider capability applies to this course.",
    show: "Show each provider",
    status: { ready: "Ready", blocked: "Not ready", unknown: "Not yet confirmed" },
    /** What keeps a capability from being ready, said once and naming who fixes it, by the server's first step. */
    missing: (code: string | undefined, provider: string): string => {
      switch (code) {
        case "reconnect_provider":
          return `Your ${provider} account isn't connected.`;
        case "resolve_provider_scope":
          return `${provider} hasn't given IMAM the access it needs. Connecting your account again asks for it.`;
        case "verify_provider_api":
        case "verify_provider_eligibility":
        case "run_provider_preflight":
          return `Your school's ${provider} administrator hasn't turned this on yet.`;
        case "repair_exact_mapping":
          return `This course isn't matched to ${provider} yet. Your administrator matches it.`;
        case "verify_tenant_enablement":
          return "IMAM hasn't turned this on for your school yet.";
        case "resolve_capability_dependency":
          return "Another connection has to be ready first.";
        default:
          return `${provider} isn't ready for this course yet. Your administrator can see why.`;
      }
    },
  },
  drive: {
    label: "Course folder",
    job: "Say where this course's materials folder is in Drive, or set one up.",
    failed: "Couldn't read the course folder",
    failedDetail: "Material links below still work.",
    stale: "Couldn't read the folder again. This is the last answer.",
    ready: "This course's materials folder is in Drive.",
    readyDetail: "Everyone in the Workspace can open its contents by link. Keep private learner work elsewhere.",
    readySub: "Use your institution's Google account to upload and edit teaching files.",
    open: "Open in Drive",
    pending: "Drive hasn't confirmed the folder yet",
    pendingSub: "It is being created. Check again shortly.",
    checkAgain: "Check again",
    unknown: "The folder's outcome isn't known.",
    unknownDetail: "An administrator must reconcile it before another can be created.",
    none: "No course folder in Drive yet.",
    noneState: "No folder",
    linkedState: "Folder linked",
    reuseFolder: "Use this course's existing Drive folder.",
    closedTerm: "The term is closed, so no folder can be set up.",
    denied: "You can't set one up for this course.",
    setUp: "Set it up",
    noneDetail: "Create one shared place for teaching files. Keep private learner work elsewhere.",
    audience: "Audience",
    audienceSummary: "Everyone in the Workspace can open this folder by link, and that audience can't be narrowed later. Don't put learner work in it.",
    commit: "Create course folder",
    cap: "When you create the course folder",
    effectFolder: "Drive creates one folder for this course.",
    effectAudience: "Everyone in the Workspace can open it by link. Keep private learner work elsewhere.",
    editing: "Adding files here does not publish them as course materials in IMAM.",
    effectEditing: "Verified assigned instructors can upload and edit teaching files.",
    effectOwnEditing: "You can upload and edit using your institution's Google account.",
    prepareEditing: "Prepare editing access",
    editingCap: "Prepare this course folder for teaching",
    created: "Course folder ready",
    folderExists: "The course folder exists. You can open it in Drive.",
    yourEditingReady: "Your editing access is ready",
    preparedState: "Editing access prepared",
    preparedDetail: "Editing was prepared for your institutional Google account. Drive controls current access.",
    teamWaiting: "Your editing was prepared. Institution support still has a colleague's verification to complete.",
    accessDetails: "Editing access",
    accessIncomplete: "Folder ready; editing needs attention",
    accessRetry: "Editing access has not been prepared yet.",
    retryEditing: "Prepare editing access",
    retryNote: "Uses the same folder and the instructors in this request.",
    verificationNeeded: "Institution support must verify a course instructor's Google account. Existing folder access remains available.",
    institutionRecovery: "Institution support must check a previous Google access change. You can still open the folder; no further permissions will be added until it is resolved.",
    verificationNote: "After verification, refresh this page to prepare a new access plan.",
    accessUnknown: "The folder exists. Google has not confirmed editing access. Check before trying again.",
    resumeRequest: "You have an earlier folder setup request. Check its result before starting another.",
    refused: "Setup not completed",
    refusedFallback: "The course folder could not be created. The material links below still work.",
    unknownResult: "Setup not confirmed",
    unknownResultSentence: "The request was sent and no answer came back. The folder may exist.",
    check: "Check folder setup",
    checkNote: "Checks this folder and its editing access. No folder or permission is created.",
  },
} as const;
